// @flow
import React, {useCallback, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/styles';
// import {useHistory} from 'react-router-dom';
import {Hidden} from '@material-ui/core';

import {colors} from '../../../../assets/styles/colors';
import {usePage} from '../../../page/utils';
import CustomEditor from '../components/CustomEditor/CustomEditor';
import HeaderTitle from '../components/HeaderTitle/HeaderTitle';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import LoadingSpinner from '../../../../elements/LoadingSpinner/LoadingSpinner';
import SnackBar from '../../../../elements/SnackBar/SnackBar';
import {useIsFeatureInTier} from '../../../../utils/component';
import {useTypedSelector} from '../../../../store';

import type {SaveLegalRequest} from '../api';
import type {Shippings as ShippingsType} from '../duck';
type Props = {
  saveStoreShippingsConfig: (saveLegalRequest: SaveLegalRequest) => void,
  fetchStoreShippingsConfig: () => void,
  isSaving?: boolean,
  shippings?: ShippingsType,
  isFetchingShippings?: boolean,
  saveShippingsSuccess?: boolean,
  saveShippingsError?: string,
};

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: '#FFFFFF',
    '& .w-md-editor-toolbar': {
      left: '0!important',
    },
    padding: theme.spacing(2, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',

    borderRadius: '12px',
    overflow: 'hidden',
  },
  containerEditor: {
    position: 'relative',
    borderRadius: 8,
    border: '1px solid #E8E8ED',
    padding: theme.spacing(1.5),
    backgroundColor: colors.grey200,
    [theme.breakpoints.up('md')]: {
      padding: 0,
    },
  },
  hiddenBlock: {
    borderRadius: '8px 8px 0 0',
    backgroundColor: colors.grey200,
    height: theme.spacing(6),
    display: 'flex',
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
      height: 0,
    },
  },
  buttonSave: {
    marginRight: theme.spacing(2),
    color: colors.grey900,
    height: 24,
    fontSize: 14,
    fontWeight: 600,
    border: '1px solid #D4D4DD',
    padding: theme.spacing(1, 3),
    backgroundColor: '#FFFFFF',
  },
}));
function Shippings(props: Props) {
  const {
    isSaving,
    shippings,
    isFetchingShippings,
    saveShippingsSuccess,
    saveShippingsError,
    saveStoreShippingsConfig,
    fetchStoreShippingsConfig,
  } = props;

  const classes = useStyles();
  const legalSetting = useTypedSelector(
    state => state.legalConfig.legalSetting
  );

  const [description, setDescription] = useState('');

  const hasShippingInformationFeature = useIsFeatureInTier('STORE_POLICIES');

  const handleSaveShippings = () => {
    if (description && description.length > 0) {
      saveStoreShippingsConfig({
        legalConfigRequest: {enabled: true, description},
      });
    }
  };

  const handleDescription = useCallback((text: string) => {
    setDescription(text);
  }, []);

  useEffect(() => {
    if (shippings && shippings.description) {
      setDescription(shippings.description);
    }
  }, [shippings]);

  useEffect(() => {
    if (hasShippingInformationFeature) {
      fetchStoreShippingsConfig();
    }
  }, [fetchStoreShippingsConfig, hasShippingInformationFeature]);

  // useEffect(() => {
  //   if (saveShippingsSuccess) history.push('/my-store/pages');
  // }, [history, saveShippingsSuccess]);

  return (
    <div className={classes.root} data-color-mode="light">
      {(isFetchingShippings || isSaving) && <LoadingSpinner />}
      <SnackBar
        mode="error"
        message={!!saveShippingsError ? saveShippingsError : ''}
      />
      <SnackBar
        mode="success"
        message={!!saveShippingsSuccess ? 'Guardado correctamente.' : ''}
      />
      <HeaderTitle
        title="Envíos y pagos"
        description="Describa cómo enviá/entrega sus productos así tambien cómo acepta pagos por los pedidos."
        enabledKeyName="shippingInformationEnabled"
        enabled={legalSetting?.shippingInformationEnabled}
        disabled={!hasShippingInformationFeature}
      />
      <div className={classes.containerEditor}>
        <div className={classes.hiddenBlock}>
          {hasShippingInformationFeature && (
            <Hidden smDown>
              <CustomButton
                disabled={!hasShippingInformationFeature}
                className={classes.buttonSave}
                onClick={handleSaveShippings}
              >
                Guardar
              </CustomButton>
            </Hidden>
          )}
        </div>
        <CustomEditor
          disabled={!hasShippingInformationFeature}
          selected
          noBorder
          idx={0}
          text={description}
          onChange={handleDescription}
        />
        {hasShippingInformationFeature && (
          <Hidden mdUp>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                margin: '1rem 0 0 0',
              }}
            >
              <CustomButton
                disabled={!hasShippingInformationFeature}
                className={classes.buttonSave}
                onClick={handleSaveShippings}
              >
                Guardar
              </CustomButton>
            </div>
          </Hidden>
        )}
      </div>
    </div>
  );
}

Shippings.propTypes = {
  saveStoreShippingsConfig: PropTypes.func.isRequired,
  fetchStoreShippingsConfig: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  shippings: PropTypes.object,
  isFetchingShippings: PropTypes.bool,
  saveShippingsSuccess: PropTypes.bool,
  saveShippingsError: PropTypes.string,
};

export default usePage<Props>(
  Shippings,
  'Shippings and payments configuration'
);
